import type { JournalData } from "@qendo/profile/journal";
import { ScaleType, TrackableType } from "@qendo/trackables";
import { Link } from "expo-router";
import { useMemo } from "react";
import { Pressable, View } from "react-native";
import { Badge } from "~/components/feedback/Badge";
import { ThumbnailGallery } from "~/components/layout/gallery/ThumbnailGallery";
import { JournalLine } from "~/components/layout/journal/JournalLine";
import { ScaleOutput } from "~/components/layout/journal/ScaleOutput";
import { Paragraphs, Subtitle, Title } from "~/components/layout/text";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items, Margin, Padding, Rounded, Width } from "~/constants/layout";
import { useOptions } from "~/providers/OptionsProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { useTrackable } from "~/providers/TrackableProvider";
import { formatTimeForHumans } from "~/util/dates";
import { extractMeasures } from "~/util/qendo/measure";

type JournalListingProps = {
    journal: JournalData;
};

export const JournalListing = ({ journal }: JournalListingProps) => {
    const { colours, background } = useTheme();
    const { findTrackable } = useTrackable();
    const { findOption } = useOptions();
    const formattedTime = formatTimeForHumans(journal.recorded_at);
    // biome-ignore lint/correctness/useExhaustiveDependencies(findTrackable): API
    const trackable = useMemo(() => findTrackable(journal.trackable_id), [journal]);
    const icon = trackable?.icon ?? "other";

    // biome-ignore lint/correctness/useExhaustiveDependencies(findOption): API
    const measurements = useMemo(() => extractMeasures(findOption, trackable, journal), [trackable, journal]);

    return (
        <Link href={{ pathname: "/journal/[id]/show", params: { id: journal.id } }} asChild>
            <Pressable>
                <View style={[Flex.row, Items.start, Gap.sm, Rounded.lg, Padding.sm, Width.full, background.primary]}>
                    <QendoIcon name={icon} size={24} color={colours.icon.secondary} />
                    <View style={[Gap.xxs, Flex.flex, Items.start]}>
                        <View style={[Flex.row, Flex.between, Items.start, Width.full]}>
                            <Title size="base">{trackable?.title}</Title>
                            <Subtitle align="right" style={Flex.shrink_0}>
                                {formattedTime}
                            </Subtitle>
                        </View>
                        {measurements.map(({ title, value, key }) => (
                            <JournalLine title={title} data={value} key={key} />
                        ))}
                        <ScaleOutput
                            scale={trackable?.scale ?? ScaleType.none}
                            label={trackable?.scale_name}
                            value={journal.scale}
                        />
                        <Paragraphs numberOfLines={3} content={journal.description} />
                        <View style={[Items.start, Gap.xs, Margin.top_xxs]}>
                            <Only when={journal.flare}>
                                <Badge title="Triggered a flare" icon="zap-filled" variant="danger" size="sm" />
                            </Only>
                            <Can ability="view" section="photos">
                                <Only
                                    when={trackable?.type === TrackableType.journal}
                                    show={() => <ThumbnailGallery images={journal.photograph_ids} />}
                                />
                            </Can>
                        </View>
                    </View>
                </View>
            </Pressable>
        </Link>
    );
};
